// Web Push (PWA) subscription management.
//
// iOS Safari requires `Notification.requestPermission()` to run directly in the
// tap gesture — `enablePushNotifications()` therefore requests permission first,
// before any other await. Web Push itself only works on HTTPS and, on iOS,
// only inside an installed (home-screen) PWA.

import { getVapidPublicKey, subscribePush, unsubscribePush } from './api'

const SW_SCOPE = '/sw.js'

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

export async function registerPushServiceWorker() {
  if (!isPushSupported()) return null
  return navigator.serviceWorker.register(SW_SCOPE)
}

export async function getPushState() {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', enabled: false }
  }
  const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  const subscription = registration ? await registration.pushManager.getSubscription() : null
  return {
    supported: true,
    permission: Notification.permission,
    enabled: Boolean(subscription),
  }
}

export async function enablePushNotifications() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  // MUST be the first await: iOS drops the prompt outside the user gesture.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied', permission }
  }

  const registration = (await navigator.serviceWorker.getRegistration(SW_SCOPE))
    || (await registerPushServiceWorker())
  if (!registration) return { ok: false, reason: 'sw-unavailable' }

  const { data: vapid } = await getVapidPublicKey()
  if (!vapid.public_key) return { ok: false, reason: 'server-unconfigured' }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
    })
  }

  const json = subscription.toJSON()
  await subscribePush({
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent,
  })

  return { ok: true, permission }
}

export async function disablePushNotifications() {
  const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  const subscription = registration ? await registration.pushManager.getSubscription() : null
  if (!subscription) return { ok: true }

  try {
    await unsubscribePush(subscription.endpoint)
  } finally {
    await subscription.unsubscribe()
  }
  return { ok: true }
}
